"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Input, Typography } from "@/components/ui";
import { NewButton } from "@/components/ui/NewButton";
import { env } from "@/lib/environment";
import { useGeoLocation } from "@/lib/hooks/useGeoLocation";
import { trackLeadMagnetLead } from "@/lib/tracking/ga";
import CountryList from "country-list-with-dial-code-and-flag";

const sanitizePhoneNumber = (value: string) => value.replace(/\D/g, "");

interface FormData {
  name: string;
  company: string;
  email: string;
  country: string;
  phone: string;
}

interface FormErrors {
  name?: string;
  company?: string;
  email?: string;
  phone?: string;
}

interface OnPremiseGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function OnPremiseGuideModal({ isOpen, onClose }: OnPremiseGuideModalProps) {
  const { geoData, isLoading: geoLoading } = useGeoLocation();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    company: "",
    email: "",
    country: "US",
    phone: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [phoneValue, setPhoneValue] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<"US" | string>("US");
  const [selectedDialCode, setSelectedDialCode] = useState<string>("+1");
  const [isFormReady, setIsFormReady] = useState(false);

  // Comprehensive country data with phone codes
  const countryData = useMemo(() => {
    const countries = CountryList.getAll();
    const countryMap = new Map();

    countries.forEach((country) => {
      const code = country.code;
      if (!countryMap.has(code)) {
        countryMap.set(code, {
          code: country.code,
          name: country.name,
          flag: country.flag,
          dialCodes: [],
        });
      }

      if (country.dialCode) {
        countryMap.get(code).dialCodes.push(country.dialCode);
      }
    });

    return Array.from(countryMap.values())
      .map((country) => ({
        ...country,
        phoneCode: country.dialCodes[0] || "+1",
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const getAvailableDialCodes = useCallback(
    (countryCode: string) => {
      const country = countryData.find((c) => c.code.toLowerCase() === countryCode.toLowerCase());
      return country?.dialCodes || ["+1"];
    },
    [countryData]
  );

  const currentCountry = useMemo(() => {
    return countryData.find((c) => c.code.toLowerCase() === formData.country.toLowerCase());
  }, [countryData, formData.country]);

  // Auto-detect country from IP
  useEffect(() => {
    if (geoData?.countryCode) {
      const detectedCountry = geoData.countryCode.toLowerCase();
      setSelectedCountry(detectedCountry);
      setFormData((prev) => ({ ...prev, country: detectedCountry }));

      const availableDialCodes = getAvailableDialCodes(detectedCountry);
      setSelectedDialCode(availableDialCodes[0]);
    }
    if (!geoLoading) {
      setIsFormReady(true);
    }
  }, [geoData, countryData, getAvailableDialCodes, geoLoading]);

  // Handle escape key and body scroll lock
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll
      document.body.style.overflow = "hidden";

      // Handle escape key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          onClose();
        }
      };

      document.addEventListener("keydown", handleEscape);

      return () => {
        document.body.style.overflow = "unset";
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isOpen, onClose]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    const sanitizedPhone = sanitizePhoneNumber(phoneValue);
    if (sanitizedPhone && (sanitizedPhone.length < 4 || sanitizedPhone.length > 15)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "country") {
      const availableDialCodes = getAvailableDialCodes(value);
      setSelectedDialCode(availableDialCodes[0]);
    }

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const grecaptcha = (window as any).grecaptcha;
      if (!grecaptcha || !grecaptcha.execute) {
        throw new Error("reCAPTCHA not loaded. Please refresh the page and try again.");
      }

      await new Promise((resolve) => setTimeout(resolve, 100));

      const token = await grecaptcha.execute(env.NEXT_PUBLIC_GRECAPTCHA_KEY, {
        action: "submit",
      });

      if (!token) {
        throw new Error("Failed to get reCAPTCHA token. Please try again.");
      }

      const sanitizedPhone = sanitizePhoneNumber(phoneValue);

      const details = {
        name: formData.name,
        email: formData.email,
        phone: sanitizedPhone ? `${selectedDialCode}${sanitizedPhone}` : "",
        company_name: formData.company,
        description: "On-premise Implementation Guide Download Request",
        token: token,
        leads_source: "On-premise Guide Download",
        address: {
          country_code: geoData?.countryCode,
          city: geoData?.city,
          region: geoData?.region,
          country: selectedCountry,
        },
      };

      const res = await fetch(env.NEXT_PUBLIC_WIDGET_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          details: details,
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();

      if (result.success) {
        setSubmitStatus("success");
        trackLeadMagnetLead();

        // Open PDF immediately (synchronous with user action to avoid popup blockers)
        // Add noopener and noreferrer for security
        const pdfWindow = window.open(
          'https://drive.google.com/file/d/1Z41z8i_SlIDKxQWgcchmmrX6pYE-Qh4F/view',
          '_blank',
          'noopener,noreferrer'
        );
        
        // Additional security: ensure opener is null
        if (pdfWindow) {
          pdfWindow.opener = null;
        }

        // Dispatch Mixpanel tracking event
        window.dispatchEvent(
          new CustomEvent("mixpanelEvent", {
            detail: {
              type: "on-premise-guide-downloaded",
              data: {
                email: formData.email,
                name: formData.name,
                country: formData.country,
                company_name: formData.company,
                phone: sanitizedPhone ? `${selectedDialCode}${sanitizedPhone}` : "",
              },
            },
          })
        );

        // Reset form and close modal after showing success message
        setTimeout(() => {
          setFormData({
            name: "",
            company: "",
            email: "",
            country: selectedCountry,
            phone: "",
          });
          setPhoneValue("");
          setSubmitStatus("idle");
          onClose();
        }, 2000);
      } else {
        throw new Error(result.message || "Submission failed");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black opacity-50 backdrop-blur-2xl transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div 
        className="flex min-h-full items-center justify-center mt-10 p-4"
        onClick={(e) => {
          // Only close if clicking the container itself, not its children
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
        onKeyDown={(e) => {
          // Close on Enter or Space when clicking the container
          if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
            e.preventDefault();
            onClose();
          }
        }}
        role="presentation"
        tabIndex={-1}
      >
        <div
          className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left shadow-xl transition-all"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div className="mb-4">
            <h3 id="modal-title" className="text-2xl font-semibold leading-6 text-text-primary">
              Free On-Premise Guide
            </h3>
          </div>

          <button
            type="button"
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-500 transition-colors"
            onClick={onClose}
          >
            <span className="sr-only">Close</span>
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

                <div className="mt-4">
                  {!isFormReady ? (
                    <div className="w-full flex items-center justify-center py-8 min-h-[300px]">
                      <div className="flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        <Typography variant="small" as="p" className="text-text-secondary">
                          Loading form...
                        </Typography>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        label="Name*"
                        placeholder="Enter your name"
                        size="md"
                        value={formData.name}
                        onChange={handleInputChange}
                        error={errors.name}
                        required
                      />

                      <Input
                        id="company"
                        name="company"
                        type="text"
                        label="Company*"
                        placeholder="Enter company name"
                        size="md"
                        value={formData.company}
                        onChange={handleInputChange}
                        error={errors.company}
                        required
                      />

                      <Input
                        id="email"
                        name="email"
                        type="email"
                        label="Email*"
                        placeholder="Enter your email here"
                        size="md"
                        value={formData.email}
                        onChange={handleInputChange}
                        error={errors.email}
                        required
                      />

                      {/* Phone Number - Optional */}
                      <div className="flex flex-col gap-1.5">
                        <label id="phone-label" className="text-sm font-medium text-text-primary">
                          Phone (Optional)
                        </label>
                        <div className="flex flex-row w-full gap-2">
                          <div className="relative">
                            <div className="w-10 h-11 rounded-lg border border-[#D7D9DE] bg-white flex items-center justify-center text-base">
                              <span aria-hidden>{currentCountry?.flag || "🇺🇸"}</span>
                            </div>
                            <select
                              id="country"
                              name="country"
                              aria-label="Country"
                              value={formData.country}
                              onChange={handleInputChange}
                              className="absolute inset-0 w-14 h-12 opacity-0 cursor-pointer"
                            >
                              {countryData.map((country) => (
                                <option key={country.code} value={country.code.toLowerCase()}>
                                  {country.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="h-11 w-full rounded-lg border border-[#D7D9DE] bg-white px-4 flex items-center gap-2 text-lg text-text-secondary">
                            {currentCountry && currentCountry.dialCodes.length > 1 ? (
                              <select
                                value={selectedDialCode}
                                onChange={(e) => setSelectedDialCode(e.target.value)}
                                className="text-text-primary bg-transparent outline-none border-none cursor-pointer text-sm md:text-base font-medium"
                              >
                                {currentCountry.dialCodes.map((dialCode: string) => (
                                  <option key={dialCode} value={dialCode}>
                                    {dialCode}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-text-primary text-sm md:text-base font-medium">
                                {selectedDialCode}
                              </span>
                            )}
                            <input
                              id="phone"
                              name="phone"
                              type="tel"
                              placeholder="Enter phone number"
                              value={phoneValue}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPhoneValue(value);
                                setErrors((prev) => {
                                  if (!prev.phone) {
                                    return prev;
                                  }
                                  return { ...prev, phone: undefined };
                                });
                              }}
                              className="w-full h-full bg-transparent outline-none text-text-primary placeholder:text-text-secondary/80 text-sm md:text-base font-medium"
                              inputMode="tel"
                              pattern="[0-9]*"
                              autoComplete="tel"
                              aria-labelledby="phone-label"
                            />
                          </div>
                        </div>
                        {errors.phone && <p className="text-sm text-red-500 mt-1">{errors.phone}</p>}
                      </div>

                      {/* Status Messages */}
                      {submitStatus === "success" && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-green-800 text-sm">
                            Thank you! Your guide is being downloaded now.
                          </p>
                        </div>
                      )}

                      {submitStatus === "error" && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-red-800 text-sm">
                            Sorry, there was an error. Please try again.
                          </p>
                        </div>
                      )}

                      <NewButton
                        type="submit"
                        variant="primary"
                        className="w-full text-lg px-0 mt-2"
                        disabled={isSubmitting}
                        size="lg"
                      >
                        {isSubmitting ? "Submitting..." : "Submit"}
                      </NewButton>
                    </form>
                  )}
                </div>
        </div>
      </div>
    </div>
  );
}

